import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS } from '@/shared/api';
import { fetchThemeConfig, LOCAL_PROJECT_CONFIG, updateGeneralSettings } from './api';
import type { InterruptionSettings, ProjectConfigResponse, TimeDisplayFormat } from './types';

/** Query-ключи для запросов настроек проекта */
export const settingsKeys = {
  /** Корневой ключ всех запросов настроек */
  all: [API_TAGS.SETTINGS] as const,
  /** Ключ запроса конфигурации тем проекта */
  themeConfig: () => [...settingsKeys.all, 'themeConfig'] as const,
};

/**
 * Хук для получения конфигурации проекта (темы в формате ProjectConfigResponse)
 *
 * @returns Query объект с данными (themes — массив, colorParameter с code в snake_case)
 */
export const useThemeConfig = () => {
  return useQuery<ProjectConfigResponse>({
    queryKey: settingsKeys.themeConfig(),
    queryFn: fetchThemeConfig,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * Действующий общесистемный формат отображения интервалов времени.
 * Пока конфигурация проекта не загружена (и при недоступности бэкенда) — значение локального конфига.
 */
export const useTimeDisplayFormat = (): TimeDisplayFormat => {
  const { data } = useThemeConfig();
  return (data ?? LOCAL_PROJECT_CONFIG).timeDisplayFormat;
};

/**
 * Действующие пороги распознавания перебиваний и пауз.
 * Пока конфигурация проекта не загружена (и при недоступности бэкенда) — значения локального конфига.
 */
export const useInterruptionSettings = (): InterruptionSettings => {
  const { data } = useThemeConfig();
  const { interruptionMinDurationMs, interruptionMaxDurationMs, pauseMinDurationMs } = data ?? LOCAL_PROJECT_CONFIG;

  return useMemo(
    () => ({ interruptionMinDurationMs, interruptionMaxDurationMs, pauseMinDurationMs }),
    [interruptionMinDurationMs, interruptionMaxDurationMs, pauseMinDurationMs],
  );
};

/**
 * Изменение общих настроек системы: по успеху конфигурация проекта перезапрашивается,
 * и новые значения начинают действовать во всех интерфейсах.
 */
export const useUpdateGeneralSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGeneralSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKeys.themeConfig() }),
  });
};
